package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.ir.model.UiNode

/**
 * 元素树位置 for 循环重组的 AST 节点。
 *
 * <p>统一表达四种语法形态：
 * <ul>
 *   <li>`for (item in xs)`：单变量，[var1]=item、[var2]=null、[overMap]=false；</li>
 *   <li>`for (item, index in xs)`：带下标，[var1]=item、[var2]=index、[overMap]=false；</li>
 *   <li>`for ((k, v) in map)`：Map 解构，[var1]=key、[var2]=value、[overMap]=true；</li>
 *   <li>`for (item in xs; key = item.customId)`：带显式 diff key，[keyExpr] 非 null。</li>
 * </ul>
 *
 * <p>[body] 必须是单根 element（多根编译期报 E845，与 [ConditionalContentAst] 的 E841 对齐）。
 *
 * <p>通过 [ElementAst.forContent] 字段挂载到包装元素上：包装元素的 kind 固定为 [KIND_FOR]，
 * children 为空，仅承载位置 / id；实际循环体由 [ForContentAst] 描述。这与 [ConditionalContentAst]
 * 的承载模式一致，避免破坏 ElementAst 的 data class 结构。
 */
data class ForContentAst(
    /** 可迭代源表达式（List / Range / 任意 Iterable，或 [overMap]=true 时的 Map）。 */
    val iterable: ExprAst,

    /** 第一个循环变量名（List/Range 形态为 item，Map 形态为 key）。 */
    val var1: String,

    /** 第二个循环变量名（List 形态为 index，Map 形态为 value）；未声明时为 null。 */
    val var2: String?,

    /** 是否为 Map 解构形态（var1=key, var2=value）；false 为 List/Range 形态。 */
    val overMap: Boolean,

    /** 循环体：单根 element。 */
    val body: ElementAst,

    override val position: SourcePosition,

    /**
     * 显式 diff key 表达式（v28，可选）。
     *
     * <p>非 null 时运行期对每个 item 求值，以结果作为 diff key，覆盖自动提取逻辑
     * （Map.id / getId() / index fallback）。语法：`for (item in xs; key = item.customId)`。
     * Map 解构形态（[overMap]=true）不支持此字段，编译期报 E849。</p>
     */
    val keyExpr: ExprAst? = null,
) : AstNode {

    companion object {
        /**
         * 包装元素的 kind 占位字符串。
         *
         * <p>直接引用 IR 层的 [UiNode.KIND_FOR] 单一来源，避免 dsl-compiler 与 dsl-ir 各自维护
         * 字符串副本——如果两边不同步，runtime 分派会失败。</p>
         */
        const val KIND_FOR: String = UiNode.KIND_FOR
    }
}
